// Dispensing, goods receipt and stock adjustment (FR-DSP-*, FR-INV-*).
// Every quantity change is written as a stock movement, so the ledger explains
// itself: each row points back at the dispense, receipt or adjustment behind it.
import type { AbstractPowerSyncDatabase } from '@powersync/web';
import type { ClinicCrypto } from '@/lib/crypto/clinic-crypto';
import { newId, nowIso } from '@/lib/db/db';
import { allocateFefo, batchesForDrug, type Allocation } from './stock';

export interface PCtx { db: AbstractPowerSyncDatabase; crypto: ClinicCrypto; clinicId: string; deviceId: string | null; userId: string | null }

export interface PendingRx {
  prescription_id: string; patient_id: string; doctor_id: string; status: string; signed_at: string | null;
  patient_no: string | null; name_my: string | null; name_en: string | null; honorific: string | null;
  doctor_name_en: string | null; doctor_name_my: string | null; item_count: number;
}

/** Prescriptions signed by a doctor and not yet fully dispensed (FR-DSP-1). */
export async function dispensingQueue(db: AbstractPowerSyncDatabase, clinicId: string): Promise<PendingRx[]> {
  return db.getAll<PendingRx>(
    `select p.id as prescription_id, p.patient_id, p.doctor_id, p.status, p.signed_at,
            i.patient_no, i.name_my, i.name_en, i.honorific,
            d.name_en as doctor_name_en, d.name_my as doctor_name_my,
            (select count(*) from prescription_items pi where pi.prescription_id = p.id) as item_count
     from prescriptions p
     left join patient_index i on i.patient_id = p.patient_id
     left join doctors d on d.id = p.doctor_id
     where p.clinic_id = ? and p.status in ('signed','partially_dispensed')
     order by p.signed_at`, [clinicId]);
}

export interface RxLine {
  id: string; drug_id: string | null; drug_name: string; quantity: number; buy_outside: number;
  dose_qty: number | null; dose_unit: string | null; frequency_code: string | null; instruction_codes: string | null; enc: string | null;
  already: number;            // quantity dispensed so far
  selling_price: number | null;
}

/** The lines of one prescription, with what has already been handed over. */
export async function prescriptionLines(db: AbstractPowerSyncDatabase, prescriptionId: string): Promise<RxLine[]> {
  return db.getAll<RxLine>(
    `select pi.id, pi.drug_id, pi.drug_name, pi.quantity, pi.buy_outside, pi.dose_qty, pi.dose_unit,
            pi.frequency_code, pi.instruction_codes, pi.enc,
            coalesce((select sum(di.qty) from dispense_items di where di.prescription_item_id = pi.id), 0) as already,
            (select dr.selling_price from drugs dr where dr.id = pi.drug_id) as selling_price
     from prescription_items pi where pi.prescription_id = ? order by pi.sort_order`, [prescriptionId]);
}

export interface DispenseLine {
  prescription_item_id: string | null;
  drug_id: string;
  qty: number;
  unit_price: number;
  substitution_reason?: string | null;
  /** Leave empty to allocate first-expiry-first-out. */
  allocations?: Allocation[];
}

export interface DispenseInput {
  prescription_id: string | null;      // null for an over-the-counter sale (FR-DSP-6)
  patient_id: string | null;
  lines: DispenseLine[];
}

/**
 * Hand medicines over and take them out of stock.
 * Allocation happens here rather than in the UI so a partial dispense, an
 * over-the-counter sale and a re-dispense all deduct stock the same way.
 */
export async function dispense(ctx: PCtx, input: DispenseInput): Promise<{ id: string; shortfalls: { drug_id: string; missing: number }[] }> {
  const dispenseId = newId();
  const ts = nowIso();
  const shortfalls: { drug_id: string; missing: number }[] = [];

  // Resolve allocations before opening the transaction: each needs its own query.
  const resolved: { line: DispenseLine; allocations: Allocation[] }[] = [];
  for (const line of input.lines) {
    if (line.qty <= 0) continue;
    let allocations = line.allocations;
    if (!allocations || allocations.length === 0) {
      const r = allocateFefo(await batchesForDrug(ctx.db, line.drug_id), line.qty);
      allocations = r.allocations;
      if (r.shortfall > 0) shortfalls.push({ drug_id: line.drug_id, missing: r.shortfall });
    }
    if (allocations.length > 0) resolved.push({ line, allocations });
  }
  if (resolved.length === 0) return { id: dispenseId, shortfalls };

  await ctx.db.writeTransaction(async (tx) => {
    await tx.execute(
      `insert into dispenses (id, clinic_id, prescription_id, patient_id, dispensed_at, status, created_at, created_by, updated_at, updated_by, device_id)
       values (?, ?, ?, ?, ?, 'completed', ?, ?, ?, ?, ?)`,
      [dispenseId, ctx.clinicId, input.prescription_id, input.patient_id, ts, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);

    for (const { line, allocations } of resolved) {
      for (const a of allocations) {
        const itemId = newId();
        await tx.execute(
          `insert into dispense_items (id, clinic_id, dispense_id, prescription_item_id, drug_id, batch_id, qty, unit_price, substitution_reason, created_at, created_by, updated_at, updated_by, device_id)
           values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [itemId, ctx.clinicId, dispenseId, line.prescription_item_id, line.drug_id, a.batch_id, a.qty, line.unit_price, line.substitution_reason ?? null, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
        await tx.execute(
          `insert into stock_movements (id, clinic_id, drug_id, batch_id, qty, kind, ref_table, ref_id, moved_at, created_at, created_by, updated_at, updated_by, device_id)
           values (?, ?, ?, ?, ?, ?, 'dispenses', ?, ?, ?, ?, ?, ?, ?)`,
          [newId(), ctx.clinicId, line.drug_id, a.batch_id, -a.qty, input.prescription_id ? 'dispense' : 'sale', dispenseId, ts, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
      }
    }
  });

  if (input.prescription_id) await refreshPrescriptionStatus(ctx, input.prescription_id);
  return { id: dispenseId, shortfalls };
}

/** Mark the prescription dispensed once every line that is not "buy outside" is covered. */
export async function refreshPrescriptionStatus(ctx: PCtx, prescriptionId: string): Promise<void> {
  const lines = await prescriptionLines(ctx.db, prescriptionId);
  const owed = lines.filter((l) => l.buy_outside !== 1);
  const anyGiven = lines.some((l) => l.already > 0);
  const allGiven = owed.length > 0 && owed.every((l) => l.already >= l.quantity);
  const status = allGiven ? 'dispensed' : anyGiven ? 'partially_dispensed' : 'signed';
  await ctx.db.execute('update prescriptions set status = ?, updated_at = ?, updated_by = ? where id = ?', [status, nowIso(), ctx.userId, prescriptionId]);
}

// -----------------------------------------------------------------------------
// Receiving stock
// -----------------------------------------------------------------------------

export interface ReceiptLine {
  drug_id: string; batch_no: string; expiry_on: string | null; qty: number; unit_cost: number; new_selling_price?: number | null;
}

export async function receiveGoods(ctx: PCtx, input: { supplier_id?: string | null; invoice_no?: string | null; received_on: string; lines: ReceiptLine[] }): Promise<string> {
  const receiptId = newId();
  const ts = nowIso();
  const total = input.lines.reduce((s, l) => s + l.qty * l.unit_cost, 0);

  // A batch is identified by drug + batch number + expiry, so a second delivery
  // of the same batch adds to it rather than creating a duplicate.
  const withBatch: { line: ReceiptLine; batchId: string; isNew: boolean }[] = [];
  for (const line of input.lines) {
    const existing = await ctx.db.getOptional<{ id: string }>(
      'select id from drug_batches where drug_id = ? and batch_no = ? and coalesce(expiry_on, \'\') = coalesce(?, \'\')',
      [line.drug_id, line.batch_no, line.expiry_on]);
    withBatch.push({ line, batchId: existing?.id ?? newId(), isNew: !existing });
  }

  await ctx.db.writeTransaction(async (tx) => {
    await tx.execute(
      `insert into goods_receipts (id, clinic_id, supplier_id, invoice_no, received_on, status, posted_at, total_cost, created_at, created_by, updated_at, updated_by, device_id)
       values (?, ?, ?, ?, ?, 'posted', ?, ?, ?, ?, ?, ?, ?)`,
      [receiptId, ctx.clinicId, input.supplier_id ?? null, input.invoice_no ?? null, input.received_on, ts, total, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);

    for (const { line, batchId, isNew } of withBatch) {
      if (isNew) {
        await tx.execute(
          `insert into drug_batches (id, clinic_id, drug_id, batch_no, expiry_on, unit_cost, created_at, created_by, updated_at, updated_by, device_id)
           values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [batchId, ctx.clinicId, line.drug_id, line.batch_no, line.expiry_on, line.unit_cost, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
      } else {
        await tx.execute('update drug_batches set unit_cost = ?, updated_at = ?, updated_by = ? where id = ?', [line.unit_cost, ts, ctx.userId, batchId]);
      }
      await tx.execute(
        `insert into goods_receipt_items (id, clinic_id, receipt_id, drug_id, batch_id, batch_no, expiry_on, qty, unit_cost, new_selling_price, created_at, created_by, updated_at, updated_by, device_id)
         values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [newId(), ctx.clinicId, receiptId, line.drug_id, batchId, line.batch_no, line.expiry_on, line.qty, line.unit_cost, line.new_selling_price ?? null, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
      await tx.execute(
        `insert into stock_movements (id, clinic_id, drug_id, batch_id, qty, kind, ref_table, ref_id, moved_at, created_at, created_by, updated_at, updated_by, device_id)
         values (?, ?, ?, ?, ?, 'receipt', 'goods_receipts', ?, ?, ?, ?, ?, ?, ?)`,
        [newId(), ctx.clinicId, line.drug_id, batchId, line.qty, receiptId, ts, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
      if (line.new_selling_price != null) {
        await tx.execute('update drugs set selling_price = ?, updated_at = ?, updated_by = ? where id = ?', [line.new_selling_price, ts, ctx.userId, line.drug_id]);
      }
    }
  });
  return receiptId;
}

export type AdjustKind = 'adjustment' | 'damage' | 'expiry_writeoff';

/** Correct a count, write off damage or expiry. Always needs a reason (FR-INV-4). */
export async function adjustStock(ctx: PCtx, input: { drug_id: string; batch_id: string; qty: number; kind: AdjustKind; reason: string }): Promise<void> {
  const ts = nowIso();
  await ctx.db.execute(
    `insert into stock_movements (id, clinic_id, drug_id, batch_id, qty, kind, reason, approved_by, moved_at, created_at, created_by, updated_at, updated_by, device_id)
     values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [newId(), ctx.clinicId, input.drug_id, input.batch_id, input.qty, input.kind, input.reason, ctx.userId, ts, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
}

// -----------------------------------------------------------------------------
// Drug master
// -----------------------------------------------------------------------------

export interface DrugInput {
  id?: string; generic_name: string; brand_name?: string | null; manufacturer?: string | null;
  strength?: string | null; form?: string | null; dispense_unit?: string; selling_price: number;
  reorder_level?: number; is_consumable?: boolean; is_controlled?: boolean; notes?: string | null;
}

export async function saveDrug(ctx: PCtx, input: DrugInput): Promise<string> {
  const ts = nowIso();
  if (input.id) {
    await ctx.db.execute(
      `update drugs set generic_name=?, brand_name=?, manufacturer=?, strength=?, form=?, dispense_unit=?, selling_price=?, reorder_level=?, is_consumable=?, is_controlled=?, notes=?, updated_at=?, updated_by=?, device_id=? where id=?`,
      [input.generic_name, input.brand_name ?? null, input.manufacturer ?? null, input.strength ?? null, input.form ?? null,
       input.dispense_unit ?? 'tablet', input.selling_price, input.reorder_level ?? 0, input.is_consumable ? 1 : 0, input.is_controlled ? 1 : 0,
       input.notes ?? null, ts, ctx.userId, ctx.deviceId, input.id]);
    return input.id;
  }
  const id = newId();
  await ctx.db.execute(
    `insert into drugs (id, clinic_id, generic_name, brand_name, manufacturer, strength, form, dispense_unit, selling_price, reorder_level, is_consumable, is_controlled, notes, active, created_at, created_by, updated_at, updated_by, device_id)
     values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 1, ?, ?, ?, ?, ?)`,
    [id, ctx.clinicId, input.generic_name, input.brand_name ?? null, input.manufacturer ?? null, input.strength ?? null, input.form ?? null,
     input.dispense_unit ?? 'tablet', input.selling_price, input.reorder_level ?? 0, input.is_consumable ? 1 : 0, input.is_controlled ? 1 : 0,
     input.notes ?? null, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
  return id;
}

export async function deactivateDrug(ctx: PCtx, id: string): Promise<void> {
  await ctx.db.execute('update drugs set active = 0, updated_at = ?, updated_by = ? where id = ?', [nowIso(), ctx.userId, id]);
}

export async function listSuppliers(db: AbstractPowerSyncDatabase, clinicId: string) {
  return db.getAll<{ id: string; name: string; phone: string | null }>(
    'select id, name, phone from suppliers where clinic_id = ? and active = 1 order by name', [clinicId]);
}

export async function saveSupplier(ctx: PCtx, name: string, phone: string | null): Promise<string> {
  const id = newId(); const ts = nowIso();
  await ctx.db.execute(
    `insert into suppliers (id, clinic_id, name, phone, active, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, 1, ?, ?, ?, ?, ?)`,
    [id, ctx.clinicId, name, phone, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
  return id;
}

/** Recent movements for one drug, newest first, for the audit view. */
export async function drugMovements(db: AbstractPowerSyncDatabase, drugId: string, limit = 50) {
  return db.getAll<{ id: string; qty: number; kind: string; reason: string | null; moved_at: string; batch_no: string; expiry_on: string | null }>(
    `select m.id, m.qty, m.kind, m.reason, m.moved_at, b.batch_no, b.expiry_on
     from stock_movements m join drug_batches b on b.id = m.batch_id
     where m.drug_id = ? order by m.moved_at desc limit ?`, [drugId, limit]);
}
